import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Clock, MessageCircle, ShieldCheck, Star, MapPin, Sparkles } from "lucide-react";

function humanizeHours(h) {
  if (h == null) return null;
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 24) return `${h.toFixed(h < 3 ? 1 : 0)} hr`;
  return `${(h / 24).toFixed(1)} days`;
}

function memberSinceLabel(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  } catch {
    return null;
  }
}

export default function AboutHost({ hostId, fallbackName, fallbackLocation }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!hostId) return;
    api.get(`/hosts/${hostId}/stats`).then((r) => setStats(r.data)).catch((err) => {
      console.error("Load host stats failed", err);
      setStats(null);
    });
  }, [hostId]);

  const name = stats?.name || fallbackName;
  const location = stats?.location || fallbackLocation;
  const bio = stats?.bio;
  const verified = stats?.is_verified_host;
  const memberSince = memberSinceLabel(stats?.member_since);
  const responseLabel = stats?.avg_response_hours != null
    ? `Typically replies in ${humanizeHours(stats.avg_response_hours)}`
    : (stats?.threads ? "New host — no reply data yet" : "No conversations yet");

  return (
    <div className="bg-white border border-subtle rounded-2xl p-6" data-testid={`about-host-${hostId}`}>
      <div className="flex items-start gap-5">
        {stats?.picture ? (
          <img src={stats.picture} alt={name} className="w-20 h-20 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-20 h-20 rounded-full bg-moss text-canvas flex items-center justify-center font-serif text-3xl shrink-0">
            {name?.charAt(0)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-serif text-2xl">{name}</div>
            {verified && (
              <span className="inline-flex items-center gap-1 text-xs bg-moss/10 text-moss rounded-full px-2 py-0.5" data-testid="host-verified-badge">
                <ShieldCheck className="h-3 w-3" strokeWidth={2} /> Verified host
              </span>
            )}
          </div>
          {location && (
            <div className="text-sm text-muted2 mt-0.5 inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" /> {location}
            </div>
          )}
          {memberSince && (
            <div className="text-xs text-muted2 mt-0.5">Hosting since {memberSince}</div>
          )}
        </div>
      </div>

      {bio && (
        <p className="mt-5 text-sm leading-relaxed text-ink whitespace-pre-line" data-testid="host-bio">{bio}</p>
      )}

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 pt-5 border-t border-subtle">
        <Stat icon={<Sparkles className="h-3.5 w-3.5" />} label="Experiences" value={stats?.experience_count ?? "—"} />
        <Stat
          icon={<Star className="h-3.5 w-3.5" />}
          label="Reviews"
          value={stats?.avg_rating ? `${stats.avg_rating} · ${stats.total_reviews}` : (stats?.total_reviews || "—")}
        />
        <Stat icon={<MessageCircle className="h-3.5 w-3.5" />} label="Conversations" value={stats?.threads ?? "—"} />
        <Stat icon={<Clock className="h-3.5 w-3.5" />} label="Response" value={humanizeHours(stats?.avg_response_hours) || "—"} />
      </div>
      <div className="mt-3 text-xs text-muted2" data-testid="host-response-time">{responseLabel}</div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div>
      <div className="text-xs text-muted2 inline-flex items-center gap-1">{icon} {label}</div>
      <div className="font-serif text-lg mt-0.5">{value}</div>
    </div>
  );
}
